// Pure publication parsing: original selections, prices and returns never depend on the clock.
import type { PbSelection, PbSlip } from "./usePowerballWeek.ts";

function parseSelection(raw: Record<string, unknown>): PbSelection {
  return {
    id: String(raw.id ?? ""),
    settlement_state: typeof raw.settlement_state === "string" ? raw.settlement_state : null,
    settlement_reason: typeof raw.settlement_reason === "string" ? raw.settlement_reason : null,
    match_name: String(raw.match_name ?? ""),
    league: String(raw.league ?? ""),
    league_short: typeof raw.league_short === "string" ? raw.league_short : null,
    country_code: typeof raw.country_code === "string" ? raw.country_code : null,
    kickoff_at: typeof raw.kickoff_at === "string" ? raw.kickoff_at : null,
    market: String(raw.market ?? ""),
    pick: String(raw.pick ?? ""),
    odds: Number(raw.odds ?? 0),
    layer: (raw.layer as PbSelection["layer"]) ?? "foundation",
    form_score: typeof raw.form_score === "number" ? raw.form_score : null,
    value_edge_pct: typeof raw.value_edge_pct === "number" ? raw.value_edge_pct : null,
    model_probability: typeof raw.model_probability === "number" ? raw.model_probability : null,
    best_platform: typeof raw.best_platform === "string" ? raw.best_platform : null,
    best_odds: typeof raw.best_odds === "number" ? raw.best_odds : null,
    outcome: typeof raw.outcome === "string" ? (raw.outcome as PbSelection["outcome"]) : null,
  };
}

export function parsePowerballSlip(raw: Record<string, unknown>): PbSlip {
  const selections = Array.isArray(raw.selections)
    ? (raw.selections as Record<string, unknown>[]).map(parseSelection)
    : [];

  const status = (raw.status as PbSlip["status"]) ?? "building";
  const result = typeof raw.result === "string" ? raw.result : null;
  const combined = Number(raw.combined_odds ?? 0);
  const potential = Number(raw.potential_win ?? 0);
  const stakeAmount = Number(raw.stake_amount ?? 0);

  return {
    validation_state: typeof raw.validation_state === "string" ? raw.validation_state : null,
    execution_eligible: raw.execution_eligible === true,
    fixture_window_start: typeof raw.fixture_window_start === "string" ? raw.fixture_window_start : null,
    fixture_window_end: typeof raw.fixture_window_end === "string" ? raw.fixture_window_end : null,
    id: String(raw.id ?? ""),
    contract_version: typeof raw.contract_version === "number" ? raw.contract_version : null,
    publication_mode: typeof raw.publication_mode === "string" ? raw.publication_mode : null,
    publication_id: typeof raw.publication_id === "string" ? raw.publication_id : null,
    publication_version: typeof raw.publication_version === "number" ? raw.publication_version : null,
    session_date: typeof raw.session_date === "string" ? raw.session_date : null,
    original_published_at: typeof raw.original_published_at === "string" ? raw.original_published_at : null,
    latest_published_at: typeof raw.latest_published_at === "string" ? raw.latest_published_at : null,
    settlement_state: typeof raw.settlement_state === "string" ? raw.settlement_state : null,
    realized_return: typeof raw.realized_return === "number" ? raw.realized_return : null,
    void_adjusted_odds: typeof raw.void_adjusted_odds === "number" ? raw.void_adjusted_odds : null,
    slip_type: (raw.slip_type as PbSlip["slip_type"]) ?? "powerball_10",
    combined_odds: combined,
    stake_tier: (raw.stake_tier as PbSlip["stake_tier"]) ?? "standard",
    stake_amount: stakeAmount,
    potential_win: potential,
    confidence_score: typeof raw.confidence_score === "number" ? raw.confidence_score : null,
    status,
    result,
    deduplication_passed:
      typeof raw.deduplication_passed === "boolean" ? raw.deduplication_passed : null,
    published_at: typeof raw.published_at === "string" ? raw.published_at : null,
    selections,
  };
}

export function selectionProgress(leg: Pick<PbSelection, "outcome" | "kickoff_at" | "settlement_state">, now = Date.now()): string {
  if (leg.settlement_state === "EXCLUDED_SYNTHETIC") return "Unavailable";
  if (leg.outcome === "win") return "Won";
  if (leg.outcome === "loss") return "Lost";
  if (leg.outcome === "void") return "Void";
  const kickoff = leg.kickoff_at ? Date.parse(leg.kickoff_at) : NaN;
  if (kickoff <= now && leg.settlement_state === "PENDING_DATA") return "Awaiting verified result data";
  return Number.isNaN(kickoff) ? "Kickoff unconfirmed" : kickoff <= now ? "Started · result pending" : "Upcoming";
}

/** Preserve the selected bookmaker's decimal precision. */
export function formatPowerballPrice(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "—";
  const text = String(value);
  return text.includes(".") ? text : `${text}.00`;
}
